/**
 * The tile under me, as the world marks it: its rim (green or red while placing, else white) and,
 * while placing, the preview of what I'm placing (ghost.ts), tinted by the rules' check
 * (game/build/placement.ts).
 */
import type { Object3D } from 'three';

import type { Loop } from '@core/loop';
import { placementCheck, type Placement } from '@game/build/placement';
import type { Game } from '@game/game';
import type { GameState } from '@game/types';

import type { Display } from '../planet/display';
import type { Planet } from '../planet/planet';
import { createGhost } from './ghost';

/** The rim of the tile I stand on. */
const rimTint = (placement: Placement | null): string =>
  placement?.state === 'ok' ? '#5FD08A' : placement?.state === 'refused' ? '#FF6B5F' : '#FFFFFF';

export interface Cursor {
  /** Mark the tile under me again: call when it, what I'm placing or the planet changed. */
  show(now: number): void;
  /** Whether a store change asks for `show`. */
  wants(s: GameState, prev: GameState): boolean;
  dispose(): void;
}

export function createCursor(
  game: Game,
  parent: Object3D,
  display: Display,
  planet: Planet,
  loop: Loop,
): Cursor {
  const ghost = createGhost(parent);
  return {
    show(now) {
      const s = game.store.get();
      const playing = display.mode === 'game';
      const placement = playing ? placementCheck(game) : null;
      ghost.show(playing ? display.game : null, s.placing, s.here, placement?.state ?? 'waiting');
      if (playing) planet.setHere(s.here, rimTint(placement), now);
      loop.wake();
    },
    wants: (s, prev) =>
      s.here !== prev.here ||
      s.placing !== prev.placing ||
      (s.placing !== null &&
        (s.planetVersion !== prev.planetVersion || s.pending !== prev.pending)),
    dispose: () => ghost.dispose(),
  };
}
